import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";

import { PermissionBit, SingleMessageStatus } from "../../api";
import { ApiConnector } from "../../api/api-connector";
import { Badge } from "../../components/ui-kit/base/badge";
import { Switch } from "../../components/ui-kit/fields/switch";
import { TextInput } from "../../components/ui-kit/fields/text-input";
import { FormFieldIdProvider } from "../../components/ui-kit/form/form-field-id-context";
import { FormModel } from "../../components/ui-kit/form/form-model";
import { FormRowEditable } from "../../components/ui-kit/form/form-row-editable";
import { CellExpandableLine } from "../../components/ui-kit/tables/cells/cell-expandable-line";
import { hasPermission } from "../../constants/permissions";
import { Patient } from "./patient";

/**
 * The announcement shown at the top of the patient's app: its text on one
 * ellipsized line, and whether the app shows it. Editing posts a new message,
 * which replaces the current one.
 */
export const PatientMessageFormRow = observer(
  function PatientMessageFormRow(props: {
    patient: Patient;
    form: FormModel;
    api: ApiConnector;
  }) {
    const { t } = useLingui();
    const { patient } = props;
    const message = patient.latestMessage;

    return (
      <FormRowEditable
        form={props.form}
        attr="message_body"
        model={message ?? {}}
        label={t`In-app message`}
        // POST /messaging: medical routers (implied by the row showing at
        // all) plus WRITE.
        editable={hasPermission(props.api.permissions, PermissionBit.WRITE)}
        getEditedField={() => ({
          message_body: message?.message_body ?? "",
          message_active: message?.message_active ?? true,
        })}
        viewMode={
          message ? (
            <div className="flex min-w-0 items-center gap-2">
              <MessageStateBadge
                active={message.message_active ?? true}
                expired={
                  patient.activeMessage?.status ===
                  SingleMessageStatus.expired_message
                }
              />
              <CellExpandableLine
                line={message.message_body}
                popoverClassName="max-h-80 w-96 max-w-[90vw] overflow-y-auto"
              >
                <div className="text-sm whitespace-pre-wrap wrap-break-word">
                  {message.message_body}
                </div>
              </CellExpandableLine>
            </div>
          ) : (
            <span className="text-muted-foreground italic">
              <Trans>None</Trans>
            </span>
          )
        }
        editMode={(editedField) => (
          <div className="flex flex-col gap-2">
            <TextInput
              attr="message_body"
              model={editedField}
              multiline="autosize"
              width={200}
            />
            {/* Its own label, so the switch doesn't take the textarea's id. */}
            <FormFieldIdProvider value={undefined}>
              <label className="flex cursor-pointer items-center gap-2 select-none">
                <Switch attr="message_active" model={editedField} />
                <Trans>Show in the patient's app</Trans>
              </label>
            </FormFieldIdProvider>
          </div>
        )}
        onChange={(editedField) => {
          const body = String(editedField.message_body ?? "").trim();
          // A blank message has nothing to announce, so it is never shown.
          void patient.saveMessage(
            body,
            !!editedField.message_active && body !== "",
          );
        }}
      />
    );
  },
);

function MessageStateBadge(props: { active: boolean; expired: boolean }) {
  if (!props.active) {
    return (
      <Badge variant="outline" className="text-muted-foreground">
        <Trans>Disabled</Trans>
      </Badge>
    );
  }
  if (props.expired) {
    return (
      <Badge className="bg-warning-soft text-warning-foreground border-warning-border">
        <Trans>Expired</Trans>
      </Badge>
    );
  }
  return (
    <Badge className="bg-success-soft text-success-foreground border-success-border">
      <Trans>Active</Trans>
    </Badge>
  );
}
